/* @flow */

import * as React from 'react'
import styled, { css } from 'styled-components'
import {
  Link,
  type Location as LocationType,
  type RouterHistory,
} from 'react-router-dom'
import querystring from 'querystring'
import sortBy from 'lodash/sortBy'

import Address from '../entities/components/Address'
import CookiesBanner from '../../infrastructure/components/CookiesBanner/CookiesBanner'
import { useCustomCss } from '../../infrastructure/hooks'
import { getPrimaryAddress } from '../entities/components/AddressSelector'
import AppContext from '../app-store/components/AppContext'
import Navigation from '../template/components/Navigation'
import { SessionContext, Tooltip } from '../shared'
import type { ConnectedBrand, WebshopSettings } from '../types'

const CreditCard = React.lazy(() => import('./components/CreditCard'))
import WebshopCustomerSelectorModal from './components/WebshopCustomerSelectorModal'
import WebshopHeader from './components/WebshopHeader'
import WebshopSessionListModal from './components/WebshopSessionListModal'
import WebshopSplash from './components/WebshopSplashV3'
import {
  WebshopDesignPageHeaderRenderer,
  WebshopDesignPageBodyRenderer,
  WebshopDesignPageRendererContext,
  useWebshopDesignPageRendererContext,
} from './components/WebshopDesignPageRenderer'
import {
  checkIfIsInWebshopEditorIframe,
  useWebshopEditorIframeMessageListener,
  WEBSHOP_EDITOR_IFRAME_GRID_ITEMS_CHANGED_MESSAGE_TYPE,
  WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_STARTED_MESSAGE_TYPE,
  WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_ENDED_MESSAGE_TYPE,
  WEBSHOP_EDITOR_IFRAME_DESIGN_PAGE_UPDATED_MESSAGE_TYPE,
  type WebshopEditorIframeGridItemsChangedMessage,
  type WebshopEditorIframeGridItemEditingStartedMessage,
} from './components/WebshopEditor/shared'

import { trackOpened, trackWebshopSessionOpened } from './api'
import { useWebshopPagesForSession } from './hooks'
import {
  WebshopContext,
  WEBSHOP_DESIGN_PAGE_MODULE_HEADER,
  WEBSHOP_DESIGN_PAGE_MODULE_SPLASH,
  WEBSHOP_DESIGN_PAGE_MODULE_LANDING_PAGE,
} from './shared'

type Props = {
  brand: ConnectedBrand,
  children?: React.Node,
  history: RouterHistory,
  location: LocationType,
  setShowRetailerSelector: boolean,
}

const WebshopTemplateV3 = ({
  brand,
  children,
  history,
  location,
  setShowRetailerSelector,
}: Props) => {
  const { entity, user } = React.useContext(SessionContext)
  const {
    availableBrand,
    currency,
    designPages,
    isDesignPagesInitialized,
    isMobile,
    isNavigationLinksInitialized,
    loadFont,
    navigationLinks,
    overrideDesignPages,
    query,
    shopUrl,
    refreshDesignPages,
    refreshNavigationLinks,
    refreshWebshopTotals,
    setUseWebshopSessionId,
    updateWebshopSession,
    webshopDesignSettings,
    webshopSession,
    webshopSettings,
  } = React.useContext(WebshopContext)

  const isFrontPage = shopUrl == location.pathname

  // Template
  const [showSessionListModal, setShowSessionListModal] = React.useState(false)
  const [showManualEditCustomerModal, setShowManualEditCustomerModal] =
    React.useState(false)
  const primaryAddress = React.useMemo(
    () => getPrimaryAddress(brand.addresses),
    [brand]
  )

  const inDesignMode = location.pathname.startsWith(shopUrl + '/editor')
  const toggleDesignMode = React.useCallback(() => {
    if (!inDesignMode) {
      history.push(shopUrl + '/editor')
    } else {
      history.push(shopUrl)
    }
  }, [inDesignMode, shopUrl])

  const footerPages = React.useMemo(() => {
    const footerPages = designPages.filter(page => {
      if (page.module !== 'page') {
        return false
      }

      if (page.content_page_show_in_footer === false) {
        return false
      }

      return true
    })
    return footerPages
  }, [designPages])

  const sortedNavigationLinks = React.useMemo(() => {
    return sortBy(navigationLinks, 'sort_order')
  }, [navigationLinks])

  const onUpdateCustomer = React.useCallback(
    (data, formikProps) => {
      return updateWebshopSession(data, formikProps).then(response => {
        if (!response.error) {
          setShowManualEditCustomerModal(false)

          refreshNavigationLinks()
          refreshWebshopTotals()
        }
      })
    },
    [
      refreshNavigationLinks,
      refreshWebshopTotals,
      setShowManualEditCustomerModal,
      updateWebshopSession,
    ]
  )

  useCustomCss(webshopSettings.custom_css)

  const onSubmitSearch = React.useCallback(
    searchQuery => {
      const newQuery = {
        // we keep the current query, so that if a customer has filtered by drop,
        // then when they search they will continue in that drop
        drops: query.drops ? query.drops : undefined,
        drop_deliveries: query.drop_deliveries
          ? query.drop_deliveries
          : undefined,
        q: searchQuery,
      }

      const newUrl = `${shopUrl}/search?${querystring.stringify(newQuery)}`
      history.push(newUrl)
    },
    [history, query, shopUrl]
  )

  const [defaultFont, setDefaultFont] = React.useState(null)

  React.useEffect(() => {
    if (webshopDesignSettings.default_font) {
      loadFont(webshopDesignSettings.default_font.id).then(font =>
        setDefaultFont(`'${font.family}', ${font.category}`)
      )

      // unset previously set font
    } else {
      setDefaultFont(null)
    }
  }, [webshopDesignSettings.default_font, setDefaultFont, loadFont])

  const { customer, drop, subbrand } = webshopSession

  React.useEffect(() => {
    if (entity && customer && entity.entity_type === 'shop') {
      trackOpened(brand.id)
    }
  }, [customer, entity])

  React.useEffect(() => {
    if (entity && customer && entity.entity_type === 'shop' && webshopSession) {
      trackWebshopSessionOpened(brand.id, webshopSession.id)
    }
  }, [entity, customer, webshopSession])

  const availableConnections = availableBrand
    ? availableBrand.retailer_connections.filter(
        connection => connection.b2b_access
      )
    : []
  const allowSelectingCustomer = entity.entity_type === 'brand'

  const showCustomerSelect = React.useMemo(() => {
    return allowSelectingCustomer && !customer
  }, [allowSelectingCustomer, customer])

  const headerDesignPage = React.useMemo(
    () => designPages.find(p => p.module === 'header'),
    [designPages]
  )

  const splashDesignPage = React.useMemo(
    () => designPages.find(p => p.module === 'splash'),
    [designPages]
  )

  const showSplash = React.useMemo(() => {
    if (!webshopSettings.splash_show_v2) {
      return false
    }

    if (!customer) {
      return false
    }

    if (!webshopSettings.splash_show) {
      return false
    }

    if (
      webshopSettings.require_drop &&
      !drop &&
      !webshopSession.drop_filter_code
    ) {
      return true
    }

    if (webshopSettings.require_subbrand && !subbrand) {
      return true
    }

    if (isFrontPage) {
      return true
    }

    return false
  }, [
    brand,
    customer,
    drop,
    isFrontPage,
    subbrand,
    webshopSession.drop_filter_code,
    webshopSettings,
  ])

  const isInWebshopEditorIframe = checkIfIsInWebshopEditorIframe()

  const previewWebshopDesignPage = React.useMemo(() => {
    // Never preview webshop design page outside of webshop editor iframe
    if (!isInWebshopEditorIframe) {
      return null
    }

    const queryParams = querystring.parse(location.search.slice(1))

    const designPageId = queryParams['preview-webshop-design-page'] || null

    return designPageId
      ? designPages.find(p => p.id === parseInt(designPageId))
      : null
  }, [designPages, isInWebshopEditorIframe])

  const isShowingSplash = React.useMemo(() => {
    // If we are previewing a design page, then we need to check if it is the splash
    if (previewWebshopDesignPage) {
      return (
        previewWebshopDesignPage.module === WEBSHOP_DESIGN_PAGE_MODULE_SPLASH
      )
    }

    return showSplash
  }, [previewWebshopDesignPage, showSplash])

  // We are showing session manager in navigation bar only when using
  // modern header
  const showSessionManagerInNavigation = headerDesignPage
    ? headerDesignPage.grid_items.find(
        gi => gi.type === 'header' && gi.data.type === 'modern'
      ) !== undefined
    : false

  const headerDesignPageOutsideProps = React.useMemo(
    () => ({
      isShowingSplash,
      onSubmitSearch,
      setShowManualEditCustomerModal,
      setShowRetailerSelector,
      setShowSessionListModal,
      showSessionManagerInNavigation,
    }),
    [
      isShowingSplash,
      onSubmitSearch,
      setShowManualEditCustomerModal,
      setShowRetailerSelector,
      setShowSessionListModal,
      showSessionManagerInNavigation,
    ]
  )

  const [
    gridItemIdBeingEditedInWebshopEditor,
    setGridItemIdBeingEditedInWebshopEditor,
  ] = React.useState<string | null>(null)

  const onWebshopEditorIframeMessageReceived = React.useCallback(
    (message: Object) => {
      switch (message.type) {
        case WEBSHOP_EDITOR_IFRAME_GRID_ITEMS_CHANGED_MESSAGE_TYPE:
          const gridItemsChangedMessage: WebshopEditorIframeGridItemsChangedMessage =
            message

          // We just replace grid items for that one design page in message using overrideDesignPages
          overrideDesignPages(state => ({
            ...state,
            entity: state.entity.map(designPage => {
              return designPage.id ===
                gridItemsChangedMessage.payload.designPageId
                ? {
                    ...designPage,
                    grid_items: gridItemsChangedMessage.payload.gridItems,
                  }
                : designPage
            }),
          }))
          break

        case WEBSHOP_EDITOR_IFRAME_DESIGN_PAGE_UPDATED_MESSAGE_TYPE:
          refreshDesignPages()
          break

        case WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_STARTED_MESSAGE_TYPE:
          const editingStartedMessage: WebshopEditorIframeGridItemEditingStartedMessage =
            message

          setGridItemIdBeingEditedInWebshopEditor(
            editingStartedMessage.payload.gridItemId
          )
          break

        case WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_ENDED_MESSAGE_TYPE:
          setGridItemIdBeingEditedInWebshopEditor(null)
          break
      }
    },
    [
      overrideDesignPages,
      refreshDesignPages,
      setGridItemIdBeingEditedInWebshopEditor,
    ]
  )
  useWebshopEditorIframeMessageListener(onWebshopEditorIframeMessageReceived)

  // Landing page shown with and also without trailing slash
  const isLandingPage =
    location.pathname === shopUrl ||
    location.pathname === `${shopUrl}/` ||
    location.pathname === `${shopUrl}/landing` ||
    location.pathname === `${shopUrl}/landing/`

  const landingClass = isLandingPage ? 'webshop-current-page-landing' : ''

  // Currently only the landing page is designed in editor
  const isPageDesignedInEditor = isLandingPage

  const webshopDesignPageRendererContextValue =
    useWebshopDesignPageRendererContext()

  if (!isNavigationLinksInitialized || !isDesignPagesInitialized) {
    return null
  }

  return (
    <div>
      {/* Using show prop for displaying so it can be rendered in advance and slide up on mobile */}
      <WebshopSessionListModal
        allowSelectingCustomer={allowSelectingCustomer}
        availableConnections={availableConnections}
        brand={brand}
        currentSession={webshopSession}
        entity={entity}
        onHide={() => setShowSessionListModal(false)}
        setUseWebshopSessionId={setUseWebshopSessionId}
        show={showSessionListModal}
        webshopSettings={webshopSettings}
      />

      {(showCustomerSelect || showManualEditCustomerModal) && (
        <WebshopCustomerSelectorModal
          currentCustomerId={webshopSession.customer_id}
          onHide={
            showManualEditCustomerModal
              ? () => setShowManualEditCustomerModal(false)
              : undefined
          }
          onSave={onUpdateCustomer}
          show={true}
          title={showCustomerSelect ? 'Select customer' : 'Change customer'}
        />
      )}

      {customer && (
        <React.Suspense fallback={<div>Loading...</div>}>
          <WebshopDesignPageRendererContext.Provider
            value={webshopDesignPageRendererContextValue}
          >
            {/*
              When the webshop is displayed inside the webshop editor iframe,
              then we dont want to show the navigation bar at all
            */}
            {!isInWebshopEditorIframe && (
              <Navigation
                fullWidth
                inWebshop
                inWebshopDesignMode={inDesignMode}
                setShowManualEditCustomerModal={setShowManualEditCustomerModal}
                setShowRetailerSelector={setShowRetailerSelector}
                setShowSessionListModal={setShowSessionListModal}
                showSessionManagerInNavigation={showSessionManagerInNavigation}
                toggleWebshopDesignMode={toggleDesignMode}
                webshopV2
              />
            )}

            {/* We need the WebshopEditor outside of containers with paddings and stuff like that */}
            {inDesignMode && <>{children}</>}

            {/** Even though it says "in design mode" here, then this is exactly the rendering of design mode,
             * but the components "in the iframe" are not "in design mode" but the outer editor component is
             */}
            {!inDesignMode && previewWebshopDesignPage && (
              <Container
                className={`webshop-main-container ${landingClass}`}
                font={defaultFont}
                fullWidth
              >
                <WebshopDesignPageHeaderRenderer
                  brand={brand}
                  gridItems={headerDesignPage?.grid_items || []}
                  editedGridItemId={gridItemIdBeingEditedInWebshopEditor}
                  outsideProps={headerDesignPageOutsideProps}
                />

                {previewWebshopDesignPage.module !==
                  WEBSHOP_DESIGN_PAGE_MODULE_HEADER && (
                  <WebshopDesignPageBodyRenderer
                    brand={brand}
                    gridItems={previewWebshopDesignPage?.grid_items || []}
                    editedGridItemId={gridItemIdBeingEditedInWebshopEditor}
                  />
                )}

                {/** This is to provide scrolling when in editor */}
                <div style={{ height: 400 }} />
              </Container>
            )}

            {/** This is normal rendering. Here we should just render the component (WebshopSplash/WebshopLanding etc) and
             * this component will then call <WebshopDesignPageBodyRenderer /> if necessary
             */}
            {!inDesignMode && !previewWebshopDesignPage && (
              <Container
                className={`webshop-main-container ${landingClass}`}
                font={defaultFont}
                fullWidth
              >
                {headerDesignPage && (
                  <WebshopDesignPageHeaderRenderer
                    brand={brand}
                    gridItems={headerDesignPage?.grid_items || []}
                    outsideProps={headerDesignPageOutsideProps}
                  />
                )}

                {showSplash && (
                  <WebshopSplash
                    outsideProps={headerDesignPageOutsideProps}
                    webshopSplashDesignPage={splashDesignPage}
                  />
                )}

                {!showSplash && isPageDesignedInEditor && <>{children}</>}

                {!showSplash && !isPageDesignedInEditor && (
                  <ContentContainer fullWidth>{children}</ContentContainer>
                )}

                <FooterContainer style={{ marginTop: 80 }}>
                  <div>
                    <Address
                      center={true}
                      address={primaryAddress}
                      entity={brand}
                    />
                  </div>
                  {footerPages.length ? (
                    <div className="text-center">
                      {footerPages.map((page, index) => {
                        return (
                          <Link key={index} to={`${shopUrl}/page/${page.id}`}>
                            {page.content_page_footer_title ||
                              page.content_page_title}
                            &nbsp;&nbsp;&nbsp;
                          </Link>
                        )
                      })}
                    </div>
                  ) : null}

                  <AppContext
                    app="Epay"
                    overrideEntity={brand}
                    render={({ app }) => {
                      if (app.settings.credit_card_icons.length === 0) {
                        return null
                      }

                      return (
                        <CreditCardContainer>
                          <CreditCard cards={app.settings.credit_card_icons} />
                        </CreditCardContainer>
                      )
                    }}
                  />
                </FooterContainer>
              </Container>
            )}

            <CookiesBanner brand={brand} />
          </WebshopDesignPageRendererContext.Provider>
        </React.Suspense>
      )}
    </div>
  )
}

export default WebshopTemplateV3

// also used by WebshopProductDetail for the fixed panel
export const fixedWidthContainer = css`
  @media (min-width: 768px) {
    width: 750px;
  }

  @media (min-width: 992px) {
    width: 970px;
  }

  @media (min-width: 1200px) {
    width: 1170px;
  }

  margin-right: auto;
  margin-left: auto;
  padding-left: 15px;
  padding-right: 15px;
`

// also used by WebshopProductDetail for the fixed panel
export const fullWidthContainer = css``

const Container = styled.div`
  ${props => (props.font ? `font-family: ${props.font};` : '')};
  ${props =>
    props.fullWidth === true ? fullWidthContainer : fixedWidthContainer};
`

const ContentContainer = styled.div.attrs({ className: 'webshop-content' })`
  min-height: 500px;
  padding-top: 25px;
  position: relative;
  ${({ fullWidth }) =>
    fullWidth === true &&
    `
    padding-left: 15px;
    padding-right: 15px;
  `}

  img {
    max-width: 100%;
  }
`

const FooterContainer = styled.div.attrs({ className: 'webshop-footer' })`
  padding: 25px 0;
`

const CreditCardContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`
